import { DeviceTypeModel } from '../interfaces/deviceTypeInterface';
import { KEYS } from '../utils/constants/storage.constants';

/**
 * Get the device manufacturer based on the user agent
 *
 * @returns
 */
export const getDeviceManufacturer = (): string => {
  const userAgent = navigator.userAgent?.toLowerCase();
  if (userAgent.includes('web0s')) {
    return 'LG Electronics Inc';
  } else {
    return 'Samsung';
  }
};

/**
 *  Get the device type based on the user agent
 *
 * @returns
 */
export const getDeviceType = (): DeviceTypeModel => {
  // check for web incase needed
  // return DeviceTypeModel.WEB;
  const userAgent = navigator.userAgent?.toLowerCase();
  if (userAgent.includes('web0s')) {
    return DeviceTypeModel.LG;
  } else {
    return DeviceTypeModel.SAMSUNG;
  }
};

/**
 *  Get the device type based on the user agent
 *
 * @returns
 */
export const getDeviceName = (): string => {
  const userAgent = navigator.userAgent?.toLowerCase();
  if (userAgent.includes('web0s')) {
    return 'LG';
  } else {
    return 'SAMSUNG';
  }
};

/**
 * Get the default model based on the user agent
 *
 * @returns
 */
export const getDefaultModel = (): string => {
  const userAgent = navigator.userAgent?.toLowerCase();
  if (userAgent.includes('web0s')) {
    return 'webOS';
  } else {
    return 'Tizen';
  }
};

/**
 * Get the device model from the window object
 * If the device model is not available, return the default model
 *
 * @returns
 */
export const getDeviceModel = (): string => {
  return window.deviceModel ? window.deviceModel : getDefaultModel();
};

/**
 * Get the app version based on the window and the platform
 *
 * @returns
 */
export const getAppVersion = (): string => {
  const userAgent = navigator.userAgent?.toLowerCase();
  if (userAgent.includes('web0s')) {
    return window.webOsVersion;
  } else {
    return window.tizenVersion;
  }
};

/**
 * Get the build version
 *
 * @returns
 */
export const getBuildVersion = (): string => {
  return window.buildVersion;
};

/**
 * Get the x-client-id based on the device type
 *
 * @returns
 */
export const getXClientId = (): string => {
  return `tm-unifitv-${getDeviceType()}`;
};

// Language code to name mapping (constant, created once)
const LANGUAGE_MAP: Readonly<Record<string, string>> = {
  en: 'English',
  te: 'Telugu',
  ta: 'Tamil',
} as const;

/**
 * Helper function to get preferred language value from localStorage.
 * Reads PREFERRED_LANGUAGES first, falls back to ANONYMOUS_PREFERRED_LANGUAGES if empty.
 *
 * @returns Language value string or null if not found
 */
const getPreferredLanguageValue = (optionalValues?: string): string | null => {
  if (optionalValues) {
    return optionalValues;
  }
  const preferredLang = localStorage.getItem(KEYS.PREFERRED_LANGUAGES);
  if (preferredLang?.trim()) {
    return preferredLang.trim();
  }

  const anonymousLang = localStorage.getItem(
    KEYS.ANONYMOUS_PREFERRED_LANGUAGES
  );
  return anonymousLang?.trim() || null;
};

/**
 * Get preferred language codes (keys) from localStorage.
 * Reads PREFERRED_LANGUAGES first, falls back to ANONYMOUS_PREFERRED_LANGUAGES if empty.
 * Returns the language codes as-is (e.g., "ta", "te", "ta,te", "te,ta").
 *
 * @returns Comma-separated language codes or undefined if no languages found
 */
export const getPreferredLanguageKeys = (): string | undefined => {
  const langValue = getPreferredLanguageValue();
  return langValue || undefined;
};

/**
 * Get preferred languages from localStorage and return their display names.
 * Reads PREFERRED_LANGUAGES first, falls back to ANONYMOUS_PREFERRED_LANGUAGES if empty.
 * Handles comma-separated language codes (e.g., "ta,te" returns "Tamil,Telugu").
 *
 * @returns Comma-separated language names or undefined if no languages found
 */
export const getPreferredLanguages = (optionalValues?: string): string | undefined => {
  const langValue = getPreferredLanguageValue(optionalValues);
  if (!langValue) {
    return undefined;
  }

  // Helper to get language name from code
  const getLanguageName = (code: string): string | null => {
    return LANGUAGE_MAP[code] || null;
  };

  // Handle comma-separated language codes
  if (langValue.includes(',')) {
    const languageNames = langValue
      .split(',')
      .map((code) => getLanguageName(code.trim()))
      .filter((name): name is string => name !== null);

    return languageNames.length > 0 ? languageNames.join(',') : undefined;
  }

  // Handle single language code
  return LANGUAGE_MAP[langValue] || undefined;
};

export const OFF = 'Off';
